import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddIcon from '@mui/icons-material/Add'
import { HubSpotLogo } from 'src/assets/integrations/HubSpotLogo'
import { useNavigate } from 'react-router-dom'
import { useGetMeAndWorkspaceQuery, useGetMeQuery } from 'src/services/user-service'
import { config } from 'src/config.js'
import { toast } from 'react-hot-toast'
import { useUpdateApiKeyMutation, useGetApiKeyMutation } from 'src/services/integration-service'
import { useSelector } from 'react-redux'
import axios from 'axios'
import { Starwhite } from 'src/icons/starwhite'
import { useGetHubspotIntegrationQuery } from 'src/services/hubspot-service'
import PremiumFeatureDialog from 'src/utils/premiumFeatureDialog'

const SuccessAiApiGenerateDialog = ({ open, setOpenSuccessaiApiGenerateDialog, handleCloseSuccessaiApiDialog, updateApiKey, isLoadingUpdateApiKey, workspace }) => {
  const handleGenerateKey = async () => {
    try {
      const toastId = toast.loading('Generating new API key', { duration: Infinity })
      const { message } = await updateApiKey({ workspace }).unwrap()
      toast.success(message, { id: toastId, duration: 2000 })
      setOpenSuccessaiApiGenerateDialog(false)
    } catch (error) {
      toast.error('Failed to generate API key. Please try again later.')
    }
  }

  return (
    <Dialog open={open} onClose={handleCloseSuccessaiApiDialog}>
      <DialogTitle sx={{ fontSize: '22px', fontWeight: '700' }}>Are you sure?</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ letterSpacing: '1px', fontWeight: 400, fontSize: '15px' }}>
          This will generate a new API key and invalidate the old one.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ margin: 2 }}>
        <Button onClick={handleCloseSuccessaiApiDialog} color='primary' variant='outlined'>
          Cancel
        </Button>
        <Button
          disabled={isLoadingUpdateApiKey}
          onClick={handleGenerateKey}
          color='primary'
          variant='contained'
        >
          Generate New API Key
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const IntegrationsApiPage = ({ tabValue, checkPlanForGrowthAndInfinty, isFreeUser, isEarlyBirdAccessUser, isAppSumoUser, planName, canEdit }) => {
  const navigate = useNavigate()
  const { data: user } = useGetMeQuery()
  const { data } = useGetMeAndWorkspaceQuery()
  const workspace = useSelector((state) => state.workspace)
  const [updateApiKey, { isLoading: isLoadingUpdateApiKey }] = useUpdateApiKeyMutation()
  const [getApiKey] = useGetApiKeyMutation()
  const [sucessaiApiKey, setSuccessaiApiKey] = useState('')
  const [openSuccessaiApiGenerateDialog, setOpenSuccessaiApiGenerateDialog] = useState(false)
  const [isZapierEnabled, setIsZapierEnabled] = useState(false)
  const [zapierKey, setZapierKey] = useState('')
  const [chromeExtentionEnabled, setChromeExtensionEnabled] = useState(false)
  const [isChromeExtensionDownloaded, setIsChromeExtensionDownloaded] = useState(false)
  const { data: HubSpotIntegrationData } = useGetHubspotIntegrationQuery()
  const [openModal, setOpenModal] = useState(false)
  const email = user?.email

  const handleClickOpenSuccessaiApiDialog = () => {
    if (isFreeUser) {
      setOpenModal(true)
      return
    }
    setOpenSuccessaiApiGenerateDialog(true)
  }

  const handleCloseSuccessaiApiDialog = () => {
    setOpenSuccessaiApiGenerateDialog(false)
  }

  const getSuccessaiApiKeyForCopy = async () => { // SuccessAi api
    const { apiKey } = await getApiKey({ workspace: workspace?._id }).unwrap()
    setSuccessaiApiKey(apiKey)
  }

  const handleSuccessaiApiCopy = async () => {
    try {
      await navigator.clipboard.writeText(sucessaiApiKey)
      toast.success('Copied to Clipboard')
    } catch (err) {
      toast.error('Unable to copy to clipboard')
    }
  }

  useEffect(() => {
    getSuccessaiApiKeyForCopy()
  }, [isLoadingUpdateApiKey, tabValue])

  const addZapierApiKey = async (checked) => {
    if (email) {
      try {
        const result = await axios.post(`${config.API_BASE_URL}/integrations/store-zapier-key`, {
          email,
          checked
        })

        if (result !== undefined) {
          setZapierKey(result?.data?.zapierApiKey)
          setIsZapierEnabled(checked)
        } else {
          throw new Error('No API key returned from server.')
        }
      } catch (error) {
        toast.error('Failed to add Zapier key. Please try again.', { duration: 2000 })
        throw error
      }
    }
  }

  const handleZapierToggle = async (event) => {
    try {
      const checked = event.target.checked
      await addZapierApiKey(checked)
    } catch (error) {
      toast.error('Failed to get Zapier key. Please try again.', { duration: 2000 })
      throw error
    }
  }

  const handleCopyZapierKey = async () => {
    try {
      await navigator.clipboard.writeText(zapierKey)
      toast.success('Copied to Clipboard')
    } catch (err) {
      toast.error('Unable to copy to clipboard')
    }
  }

  const getUserIntegration = async (email) => {
    try {
      const result = await axios.get(
        `${config.API_BASE_URL}/integrations/get-user-integrations/${email}`
      )
      if (result !== undefined) {
        setIsZapierEnabled(result?.data?.[0]?.zapierVerifiedFlag === 'Y')
        setZapierKey(result?.data?.[0]?.zapierApiKey)
      } else {
        throw new Error('No Zapier API key returned from server.')
      }
    } catch (error) {
      toast.error('Failed to get Zapier key. Please try again.', { duration: 2000 })
      throw error
    }
  }

  getUserIntegration(email)

  const handleChromeExtensionToggle = () => {
    setChromeExtensionEnabled(!chromeExtentionEnabled)
  }

  const handleChromeExtensionDownload = () => {
    setIsChromeExtensionDownloaded()
  }
  const handleNavigate = () => {
    navigate('/integrations/hubspot-settings')
  }

  const handleExplorePlans = () => {
    navigate('/settingsUpdated')
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  return (
    <Box sx={{ mt: '24px', width: '100%' }}>
      {/* Grid Layout for Cards */}
      <Grid container spacing={{ xs: 3, md: 2, lg: 3 }}>

        {/* Success.ai API Card */}
        <Grid item xs={12} sm={12} md={4}>
          <Card
            variant='outlined' sx={{
              display: 'flex',
              flexDirection: 'column',
              height: { xs: '362px', md: '425px', lg: '362px' },
              pt: '16px',
              pr: '16px',
              pb: '20px',
              pl: '16px',
              border: '1px solid #D7D9E6',
              borderRadius: '8px'
            }}
          >
            <CardContent sx={{ p: 0, height: '100%' }}>
              <Box sx={{ width: '100%', height: '150px', borderRadius: '6px' }}>
                <img
                  src='/assets/SuccessAiApiFrame.jpg'
                  alt='Zapier'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                    loading: 'lazy'
                  }}
                />
              </Box>

              <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, mt: '16px' }}>Success.ai API</Typography>
              <Typography color='text.secondary' sx={{ mt: '4px', fontSize: { xs: '12px', md: '13px' }, fontWeight: 400 }}>
                Leverage the Success.ai API to harness the full power of our tool and seamlessly connect it to your services
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column', lg: 'row' }, mt: '16px', width: '100%', justifyContent: 'space-between', alignItems: { xs: 'center', md: 'flex-start', lg: 'center' } }}>
                {
                  checkPlanForGrowthAndInfinty() || isEarlyBirdAccessUser || isAppSumoUser
                    ? (
                      <Button
                        sx={{
                          height: '34px',
                          width: '183px',
                          borderRadius: '6px',
                          gap: '1px'
                          // p: 0
                        }}
                        variant='contained'
                        onClick={handleClickOpenSuccessaiApiDialog}
                        disabled={!canEdit}
                      >
                        {sucessaiApiKey ? <RefreshIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} /> : <AddIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />}
                        <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, color: '#fff' }}>{sucessaiApiKey ? 'Regenerate API Key' : 'Generate API Key'}</Typography>
                      </Button>
                      )
                    : (
                      <Button
                        sx={{
                          height: '34px',
                          width: '183px',
                          borderRadius: '6px',
                          gap: '2px'
                          // p: 0
                        }}
                        variant='contained'
                        onClick={handleExplorePlans}
                      >
                        <Starwhite sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, color: '#fff' }}>Explore Plans</Typography>
                      </Button>
                      )
                }
                <Typography
                  variant='body2' sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 600
                  }}
                >
                  <Link
                    href={`${config.API_BASE_URL}/docs`}
                    color='primary'
                    underline='none'
                    target='_blank'
                    rel='noreferrer'
                  >
                    See Documentation
                  </Link>
                </Typography>
              </Box>

              {sucessaiApiKey && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: '0.5rem',
                    height: '40px',
                    width: '100%',
                    border: '1px solid #E4E4E5',
                    borderRadius: '8px',
                    bgcolor: '#F2F4F6',
                    pt: '10px',
                    pr: '4px',
                    pl: '10px',
                    pb: '10px'
                  }}
                >
                  <Typography sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 500,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: 'calc(100%-150px)' // Prevent button from shrinking
                  }}
                  >
                    {sucessaiApiKey}
                  </Typography>
                  <Button
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '34px',
                      width: '141px',
                      bgcolor: '#fff',
                      border: '1px solid #E4E4E5',
                      borderRadius: '6px',
                      flexShrink: 0 // Prevent button from shrinking
                    }}
                    disabled={!sucessaiApiKey || isFreeUser || !canEdit}
                    onClick={handleSuccessaiApiCopy}
                  >
                    <ContentCopyIcon fontSize='small' />
                    <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, color: '#101010' }}>Copy API Key</Typography>
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Zapier Card */}
        <Grid item xs={12} sm={12} md={4}>
          <Card
            variant='outlined' sx={{
              display: 'flex',
              flexDirection: 'column',
              height: { xs: '362px', md: '425px', lg: '362px' },
              pt: '16px',
              pr: '16px',
              pb: '20px',
              pl: '16px',
              border: '1px solid #D7D9E6',
              borderRadius: '8px'
            }}
          >
            <CardContent sx={{ p: 0, height: '100%' }}>
              <Box sx={{ width: '100%', height: '150px', borderRadius: '6px' }}>
                <img
                  src='/assets/zapierFrame.jpg'
                  alt='Zapier'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                    loading: 'lazy'
                  }}
                />
              </Box>

              <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, mt: '16px' }}>Zapier</Typography>
              <Typography color='text.secondary' sx={{ mt: '4px', fontSize: { xs: '12px', md: '13px' }, fontWeight: 400 }}>
                Integrate your Zapier automations with Success.ai to simplify and automate your daily tasks effortlessly
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '16px', height: '34px' }}>
                <FormControlLabel
                  control={<Switch checked={isZapierEnabled} onChange={handleZapierToggle} name='enable_disable_zapier' />}
                  label='Enable Zapier'
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
                {/* <Button variant="text" sx={{ fontWeight: 'medium' }}>Manage</Button> */}
              </Box>

              {isZapierEnabled && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: '0.5rem',
                    height: '40px',
                    width: '100%',
                    border: '1px solid #E4E4E5',
                    borderRadius: '8px',
                    bgcolor: '#F2F4F6',
                    pt: '10px',
                    pr: '4px',
                    pl: '10px',
                    pb: '10px'
                  }}
                >
                  <Typography sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 500,
                    maxWidth: 'calc(100% - 150px)', // Reserve space for button
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  >
                    {zapierKey}
                  </Typography>
                  <Button
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '34px',
                      width: '141px',
                      bgcolor: '#fff',
                      border: '1px solid #E4E4E5',
                      borderRadius: '6px',
                      flexShrink: 0
                    }}
                    onClick={handleCopyZapierKey}
                  >
                    <ContentCopyIcon fontSize='small' />
                    <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, color: '#101010' }}>Copy API Key</Typography>
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Warmup Connector Card */}
        {/* <Grid item xs={12} sm={12} md={4}>
          <Card
            variant='outlined' sx={{
              display: 'flex',
              flexDirection: 'column',
              height: { xs: '362px', md: '425px', lg: '362px' },
              pt: '16px',
              pr: '16px',
              pb: '20px',
              pl: '16px',
              border: '1px solid #D7D9E6',
              borderRadius: '8px'
            }}
          >
            <CardContent sx={{ p: 0, height: '100%' }}>
              <Box sx={{ width: '100%', height: '150px', borderRadius: '6px' }}>
                <img
                  src='/assets/warmupConnectorFrame.jpg'
                  alt='Zapier'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                    loading: 'lazy'
                  }}
                />
              </Box>

              <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, mt: '16px' }}>Warmup Connector</Typography>
              <Typography color='text.secondary' sx={{ mt: '4px', fontSize: { xs: '12px', md: '13px' }, fontWeight: 400 }}>
                Boost your email deliverability to keep your messages out of the spam folder
              </Typography>

              <Button
                variant='contained'
                sx={{
                  mt: '16px',
                  height: '34px',
                  width: { xs: '100%', md: '192px' },
                  fontSize: { xs: '12px', md: '14px' },
                  fontWeight: 600,
                  p: 0,
                  borderRadius: '6px'
                }}
              >
                Run Warmup Connector
              </Button>
            </CardContent>
          </Card>
        </Grid> */}

        {/* Hubspot Card */}
        <Grid item xs={12} sm={12} md={4}>
          <Card
            variant='outlined' sx={{
              display: 'flex',
              flexDirection: 'column',
              height: { xs: '362px', md: '425px', lg: '362px' },
              pt: '16px',
              pr: '16px',
              pb: '20px',
              pl: '16px',
              border: '1px solid #D7D9E6',
              borderRadius: '8px'
            }}
          >
            <CardContent sx={{ p: 0, height: '100%' }}>
              <Box sx={{ width: '100%', height: '150px', border: '1px solid #D7D9E6', borderRadius: '6px', display: 'flex', placeItems: 'center' }}>
                <HubSpotLogo height='50%' width='50%' />
              </Box>

              <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, mt: '16px' }}>Hubspot</Typography>
              <Typography color='text.secondary' sx={{ mt: '4px', fontSize: { xs: '12px', md: '13px' }, fontWeight: 400 }}>
                Connect your Hubspot CRM with Success.ai to streamline sales and marketing workflows and enhance customer relationship management.
              </Typography>

              {
                checkPlanForGrowthAndInfinty() || isEarlyBirdAccessUser || isAppSumoUser
                  ? (
                    <Button
                      variant='contained'
                      sx={{
                        mt: '16px',
                        height: '34px',
                        width: { xs: '100%', md: '192px' },
                        fontSize: { xs: '12px', md: '14px' },
                        fontWeight: 600,
                        p: 0,
                        borderRadius: '6px'
                      }}
                      href={!HubSpotIntegrationData?.data ? config.HUBSPOT_LINK : undefined}
                      target={!HubSpotIntegrationData?.data ? '_blank' : undefined}
                      onClick={HubSpotIntegrationData?.data ? handleNavigate : undefined}
                    >
                      {!HubSpotIntegrationData?.data ? ' Start Integration' : 'HubSpot Settings'}
                    </Button>
                    )
                  : (
                    <Button
                      sx={{
                        mt: '16px',
                        height: '34px',
                        width: { xs: '100%', md: '192px' },
                        fontSize: { xs: '12px', md: '14px' },
                        fontWeight: 600,
                        p: 0,
                        borderRadius: '6px'
                      }}
                      variant='contained'
                      onClick={handleExplorePlans}
                    >
                      <Starwhite sx={{ fontSize: '16px' }} />
                      <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, color: '#fff' }}>Explore Plans</Typography>
                    </Button>
                    )
              }
            </CardContent>
          </Card>
        </Grid>

        {/* Chrome Extension Card */}
        <Grid item xs={12} sm={12} md={4}>
          <Tooltip title='Coming Soon' arrow placement='top'>
            <Card
              variant='outlined' sx={{
                display: 'flex',
                flexDirection: 'column',
                height: { xs: '362px', md: '425px', lg: '362px' },
                pt: '16px',
                pr: '16px',
                pb: '20px',
                pl: '16px',
                border: '1px solid #D7D9E6',
                borderRadius: '8px'
              }}
            >
              <CardContent sx={{ p: 0, height: '100%' }}>
                <Box sx={{ width: '100%', height: '150px', borderRadius: '8px' }}>
                  <img
                    src='/assets/img.png'
                    alt='Chrome Extension'
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'fill',
                      loading: 'lazy'
                    }}
                  />
                </Box>

                <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600, mt: '16px' }}>Chrome Extension</Typography>
                <Typography color='text.secondary' sx={{ mt: '4px', fontSize: { xs: '12px', md: '13px' }, fontWeight: 400 }}>
                  Access Success.ai tools directly from your browser to capture leads, manage contacts, and boost productivity.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '16px', height: '34px' }}>
                  <FormControlLabel
                    control={<Switch checked={chromeExtentionEnabled} onChange={handleChromeExtensionToggle} />}
                    label='Enable Chrome Extension'
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                  />
                </Box>

                {chromeExtentionEnabled && (
                  <Button
                    fullWidth
                    variant='contained'
                    sx={{
                      mt: '16px',
                      height: '34px',
                      borderRadius: '6px',
                      fontSize: { xs: '12px', md: '14px' },
                      fontWeight: 600
                    }}
                    startIcon={
                      <img
                        src='/assets/Icon-1.png' // Replace with your actual image path
                        alt='Download Icon'
                        style={{ width: '15px', height: '15px' }}
                      />
                    }
                    onClick={handleChromeExtensionDownload}
                    disabled={isChromeExtensionDownloaded}
                  >
                    {isChromeExtensionDownloaded ? 'Downloaded' : 'Download Extension'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

      </Grid>

      {/* SuccessAi API Generate Dialog */}
      <SuccessAiApiGenerateDialog
        open={openSuccessaiApiGenerateDialog}
        setOpenSuccessaiApiGenerateDialog={setOpenSuccessaiApiGenerateDialog}
        handleCloseSuccessaiApiDialog={handleCloseSuccessaiApiDialog}
        updateApiKey={updateApiKey}
        isLoadingUpdateApiKey={isLoadingUpdateApiKey}
        workspace={workspace}
      />

      <PremiumFeatureDialog open={openModal} onClose={handleCloseModal} />
    </Box>
  )
}

export default IntegrationsApiPage
